/* eslint-disable @typescript-eslint/naming-convention, jsdoc/require-param, jsdoc/require-returns */
import { getPropertyEntries } from "./propertyTemplates";
import { findChildByName, getPropertyValue, cleanFBXName, getNodeArray } from "../types/fbxTypes";
import { getChildren } from "./connections";
/**
 * Extract all blend shape deformers from the FBX scene.
 */
export function extractBlendShapes(objectMap) {
    const blendShapes = [];
    for (const [id, node] of Array.from(objectMap.objects)) {
        if (node.name === "Deformer" && getPropertyValue(node, 2) === "BlendShape") {
            const bs = extractBlendShape(id, node, objectMap);
            if (bs) {
                blendShapes.push(bs);
            }
        }
    }
    return blendShapes;
}
function extractBlendShape(deformerId, _deformerNode, objectMap) {
    // Find the geometry this blend shape is attached to
    const parent = objectMap.parentOf.get(deformerId);
    if (!parent) {
        return null;
    }
    const parentNode = objectMap.objects.get(parent.id);
    if (!parentNode || parentNode.name !== "Geometry") {
        return null;
    }
    const geometryId = parent.id;
    // Find BlendShapeChannel children
    const channels = [];
    const channelChildren = getChildren(objectMap, deformerId, "Deformer");
    for (const { id: channelId, node: channelNode } of channelChildren) {
        const subType = getPropertyValue(channelNode, 2);
        if (subType !== "BlendShapeChannel") {
            continue;
        }
        const channelName = cleanFBXName(getPropertyValue(channelNode, 1) ?? "MorphTarget");
        // Read DeformPercent from Properties70
        let deformPercent = 0;
        for (const entry of getPropertyEntries(channelNode)) {
            if (entry.name === "DeformPercent" && typeof entry.values[0] === "number") {
                deformPercent = entry.values[0];
            }
        }
        const rawFullWeights = extractFullWeights(channelNode);
        // Find connected Shape geometries
        const shapes = [];
        const shapeChildren = getChildren(objectMap, channelId, "Geometry");
        for (const { node: shapeNode } of shapeChildren) {
            const shapeSubType = getPropertyValue(shapeNode, 2);
            if (shapeSubType !== "Shape") {
                continue;
            }
            const shape = extractShape(shapeNode);
            if (shape) {
                shapes.push(shape);
            }
        }
        if (shapes.length > 0) {
            const diagnostics = [];
            const fullWeights = normalizeFullWeights(rawFullWeights, shapes, channelId, channelName, diagnostics);
            channels.push({
                name: channelName,
                id: channelId,
                deformPercent,
                shapes: sortShapesByFullWeight(shapes, fullWeights),
                fullWeights: fullWeights ? [...fullWeights].sort((a, b) => a - b) : null,
                diagnostics,
            });
        }
    }
    if (channels.length === 0) {
        return null;
    }
    return {
        id: deformerId,
        geometryId,
        channels,
    };
}
function extractFullWeights(channelNode) {
    const fullWeightsNode = findChildByName(channelNode, "FullWeights");
    const rawFullWeights = getNodeArray(fullWeightsNode);
    if (!rawFullWeights) {
        return null;
    }
    if (rawFullWeights instanceof Float64Array || rawFullWeights instanceof Float32Array || rawFullWeights instanceof Int32Array) {
        return Array.from(rawFullWeights, (value) => Number(value));
    }
    return null;
}
function normalizeFullWeights(fullWeights, shapes, channelId, channelName, diagnostics) {
    if (!fullWeights) {
        if (shapes.length > 1) {
            diagnostics.push({
                type: "missing-full-weights",
                message: "Blend shape channel has multiple shapes but no FullWeights; using the first shape for compatibility.",
                channelId,
                channelName,
            });
        }
        return null;
    }
    if (fullWeights.length !== shapes.length) {
        if (shapes.length === 1) {
            return null;
        }
        diagnostics.push({
            type: "full-weights-mismatch",
            message: `FullWeights length ${fullWeights.length} does not match shape count ${shapes.length}; using the first shape for compatibility.`,
            channelId,
            channelName,
        });
        return null;
    }
    return fullWeights;
}
function sortShapesByFullWeight(shapes, fullWeights) {
    if (!fullWeights || fullWeights.length !== shapes.length) {
        return shapes.length > 1 ? [shapes[0]] : shapes;
    }
    return shapes
        .map((shape, index) => ({ shape, weight: fullWeights[index] }))
        .sort((a, b) => a.weight - b.weight)
        .map((entry) => entry.shape);
}
function extractShape(shapeNode) {
    // Shape has: Indexes (sparse vertex indices), Vertices (delta offsets from base), Normals (optional delta)
    const indexesNode = findChildByName(shapeNode, "Indexes");
    const verticesNode = findChildByName(shapeNode, "Vertices");
    if (!indexesNode || !verticesNode) {
        return null;
    }
    const rawIndices = getNodeArray(indexesNode);
    const rawVertices = getNodeArray(verticesNode);
    if (!rawIndices || !rawVertices) {
        return null;
    }
    const indices = toUint32Array(rawIndices);
    if (!indices) {
        return null;
    }
    // Convert vertices
    let vertices;
    if (rawVertices instanceof Float64Array) {
        vertices = rawVertices;
    }
    else if (rawVertices instanceof Float32Array) {
        vertices = new Float64Array(rawVertices);
    }
    else {
        return null;
    }
    // Optional normals
    let normals = null;
    const normalsNode = findChildByName(shapeNode, "Normals");
    if (normalsNode) {
        const rawNormals = getNodeArray(normalsNode);
        if (rawNormals instanceof Float64Array) {
            normals = rawNormals;
        }
        else if (rawNormals instanceof Float32Array) {
            normals = new Float64Array(rawNormals);
        }
    }
    return { indices, vertices, normals };
}
function toUint32Array(value) {
    if (value instanceof Uint32Array) {
        return value;
    }
    if (value instanceof Int32Array || value instanceof Float32Array || value instanceof Float64Array) {
        const result = new Uint32Array(value.length);
        for (let i = 0; i < value.length; i++) {
            result[i] = value[i];
        }
        return result;
    }
    return null;
}
//# sourceMappingURL=blendShapes.js.map